/**
 * Shared workflow status badge utilities.
 *
 * Provides consistent label, colour and border-class mappings for
 * workflow statuses across the entire frontend.
 */

// ── Label mappings ──────────────────────────────────────────────────

/** User-friendly labels for workflow cards/badges (Aanvragen, Sourcing, Beoordelen) */
const WORKFLOW_LABELS = {
  nieuw: 'Nieuw',
  ingediend: 'Aangeboden',
  in_review: 'In review',
  goedgekeurd: 'Gematched!',
  afgekeurd: 'Geen Match',
  te_bespreken: 'Te Bespreken',
  heraanvraag: 'Heraanvraag'
}

/** Standard / formal Dutch labels (history, project-detail) */
const STANDARD_LABELS = {
  nieuw: 'Nieuw',
  ingediend: 'Ingediend',
  in_review: 'In beoordeling',
  goedgekeurd: 'Goedgekeurd',
  afgekeurd: 'Afgekeurd',
  te_bespreken: 'Te bespreken',
  heraanvraag: 'Heraanvraag'
}

// ── Badge colour classes (with dark-mode) ───────────────────────────

const STATUS_BADGE_CLASSES = {
  nieuw: 'bg-gray-100 text-gray-600 dark:bg-gray-900/50 dark:text-gray-300',
  ingediend: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  in_review: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
  goedgekeurd: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  afgekeurd: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  te_bespreken: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
  heraanvraag: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
}

const DEFAULT_BADGE_CLASS = 'bg-gray-100 text-gray-700 dark:bg-gray-900/50 dark:text-gray-300'

// ── Border colour classes ───────────────────────────────────────────

const STATUS_BORDER_CLASSES = {
  ingediend: 'border-blue-500',
  in_review: 'border-yellow-500',
  goedgekeurd: 'border-green-500',
  afgekeurd: 'border-red-500',
  te_bespreken: 'border-orange-500',
  heraanvraag: 'border-purple-500'
}

const DEFAULT_BORDER_CLASS = 'border-gray-300'

// ── Public helpers ──────────────────────────────────────────────────

/**
 * User-friendly workflow label (for cards & badge text).
 * Returns `null` when status is falsy or 'nieuw' and isUrgent is false
 * so callers can conditionally hide the badge.
 */
export function getWorkflowStatusLabel(status, { isUrgent = false } = {}) {
  if (isUrgent) return 'SPOED'
  if (!status || status === 'nieuw') return null
  return WORKFLOW_LABELS[status] || null
}

/**
 * Standard Dutch label (for history & project-detail context).
 */
export function getStandardStatusLabel(status) {
  return STANDARD_LABELS[status] || status
}

/**
 * Tailwind badge-colour classes (background + text + dark mode).
 */
export function getWorkflowStatusBadgeClass(status, { isUrgent = false } = {}) {
  if (isUrgent) return 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
  return STATUS_BADGE_CLASSES[status] || DEFAULT_BADGE_CLASS
}

/**
 * Tailwind border-colour class.
 */
export function getWorkflowBorderColor(status, { isUrgent = false } = {}) {
  if (isUrgent) return 'border-red-500'
  return STATUS_BORDER_CLASSES[status] || DEFAULT_BORDER_CLASS
}
